import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import { useAlert } from "../../functions/useAlert";
import { DataContext } from "../../contexts/DataProvider";
import { sanitizeDisplayHtml } from "../../utils/sanitizeHtml";
import { savePptToCache } from "../../utils/pptCache";
import {
  fetchAllDocTemplates,
  createDocTemplate,
  updateDocTemplate,
  deleteDocTemplate,
  cloneDocTemplate,
  toggleDocTemplateGlobal,
  analyzeUploadedTemplate,
} from "../../services/docTemplates.service";
import {
  fetchPptTemplates,
  uploadPptTemplate,
  deletePptTemplate,
  setDefaultPptTemplate,
} from "../../services/ppt.service";
import {
  createEmptySection,
  hydrateTemplateSections,
  templateSectionsToMarkdown,
  coalesceMarkdown,
  normalizeMarkdown,
  findChangedSectionTitle,
  type TemplateSection,
  type DocTemplateListItem,
  type DocTemplateFormData,
} from "../templateSerializer";
import { useSkillChangeListener } from "../../hooks/useSkillChangeListener";

/** Owns the whole create/read/update/delete/edit/upload lifecycle for the template currently being worked on. */
export const useDocTemplateManager = (
  selectedOrganization: string | undefined,
  activeTab: number,
  setActiveTab: (tab: number) => void,
) => {
  const [setAlert] = useAlert();
  const { login }: any = useContext(DataContext);
  const isGlobalAdmin = login?.data?.role === "global admin";

  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateData, setTemplateData] = useState<DocTemplateFormData>({
    templateName: "",
    templateType: "",
    templateContent: "",
    rawMarkdown: "",
  });
  const [list, setList] = useState<DocTemplateListItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [pendingDeleteTemplateId, setPendingDeleteTemplateId] = useState<
    string | null
  >(null);

  const [searchText, setSearchText] = useState("");
  const monacoRef = useRef(null);
  const [editorInstance, setEditorInstance] = useState(null);
  const [decorations, setDecorations] = useState([]);
  const [monacoContent, setMonacoContent] = useState("");
  const [templateSections, setTemplateSections] = useState<TemplateSection[]>([
    createEmptySection(),
  ]);
  const [editorMode, setEditorMode] = useState<"sections" | "markdown">(
    "sections",
  );
  const [sectionsDirty, setSectionsDirty] = useState(false);
  const editorModeRef = useRef(editorMode);
  const sectionsParseSourceRef = useRef("");
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const monacoPreviewScrollTimer = useRef<number | null>(null);
  const [previewScrollTarget, setPreviewScrollTarget] = useState<
    string | null
  >(null);
  const [powerPointFile, setPowerPointFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pptSwitch, setPptSwitch] = useState<boolean>(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importAnalyzing, setImportAnalyzing] = useState(false);
  /** Bumped on mount / template change / import so section cards start collapsed. */
  const [sectionsCollapseToken, setSectionsCollapseToken] = useState(0);

  useEffect(() => {
    setSectionsCollapseToken((t) => t + 1);
  }, []);

  const usesSectionEditor =
    activeTab === 0 || activeTab === 1 || activeTab === 5 || activeTab === 6;
  const editorMarkdown =
    usesSectionEditor && editorMode === "sections" && sectionsDirty
      ? templateSectionsToMarkdown(templateSections)
      : monacoContent;

  const previewHtml = useMemo(
    () =>
      sanitizeDisplayHtml(
        marked.parse(editorMarkdown || "", {
          breaks: true,
          gfm: true,
        }) as string,
      ),
    [editorMarkdown],
  );

  const isReadOnlyMode =
    selectedIndex !== null &&
    !isGlobalAdmin &&
    list[selectedIndex]?.templateUploadedBy === "global admin";

  useEffect(() => {
    editorModeRef.current = editorMode;
  }, [editorMode]);

  useEffect(() => {
    if (!previewScrollTarget) return;
    if (activeTab === 2 || templateData.templateType === "powerpoint") return;

    const timer = window.setTimeout(() => {
      const root = previewContainerRef.current;
      if (!root) return;
      const needle = previewScrollTarget.trim().toLowerCase();
      if (!needle) return;

      const headings = Array.from(root.querySelectorAll("h1,h2,h3,h4,h5,h6"));
      let scrolled = false;
      for (const el of headings) {
        if (el.textContent?.trim().toLowerCase() === needle) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          scrolled = true;
          break;
        }
      }
      if (!scrolled) {
        const blocks = Array.from(
          root.querySelectorAll("p, li, strong, h1, h2, h3, h4, h5, h6"),
        );
        for (const el of blocks) {
          const text = el.textContent?.trim().toLowerCase() ?? "";
          if (text === needle || text.startsWith(`${needle}:`)) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            break;
          }
        }
      }
      setPreviewScrollTarget(null);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [editorMarkdown, previewScrollTarget, activeTab, templateData.templateType]);

  // --- PowerPoint File Handlers ---
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Accept both .pptx and .potx (check extension first, then mime as fallback)
      const name = (file.name || "").toLowerCase();
      const isPptx = name.endsWith(".pptx");
      const isPotx = name.endsWith(".potx");
      const acceptedMimeTypes = [
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.openxmlformats-officedocument.presentationml.template",
      ];
      const isAcceptedMime = acceptedMimeTypes.includes(file.type);

      if (isPptx || isPotx || isAcceptedMime) {
        setPowerPointFile(file);
        // If it's a new template, auto-fill the name
        if (selectedIndex === null) {
          setTemplateData((prev) => ({
            ...prev,
            templateName: file.name.replace(/\.pptx$/i, ""),
          }));
        }
        // ensure template type is set to powerpoint so UI switches to PPT mode
        setTemplateData((prev) => ({
          ...prev,
          templateType: "powerpoint",
        }));

        // Auto-save/upload the selected PPT/POTX immediately
        // Use a non-blocking fire-and-handle pattern so the UI stays responsive
        (async () => {
          try {
            await uploadSelectedPpt(file);
          } catch (err) {
            console.error("Auto-upload failed:", err);
            setAlert(
              "Auto-upload failed. Please try uploading manually.",
              "error",
              "error",
            );
          }
        })();
      } else {
        setAlert("Please upload a valid .pptx file.", "error");
      }
    }
    // Clear the input value to allow re-uploading the same file
    if (e.target) {
      e.target.value = null;
    }
  };

  // Upload helper used for auto-upload on select and when user clicks Save/Update
  const uploadSelectedPpt = async (file: File) => {
    // STRICT VALIDATION: Global admin must select a specific organization
    if (
      isGlobalAdmin &&
      (!selectedOrganization || selectedOrganization === "All Organisation")
    ) {
      setAlert(
        "Please select a specific organization before uploading PowerPoint templates.",
        "error",
      );
      throw new Error("Organization not selected for PowerPoint upload");
    }

    setLoading(true);
    try {
      // Determine orgId to send
      let orgIdToSend;
      if (isGlobalAdmin) {
        // For global admin, use the selected organization (never "All Organisation" due to validation above)
        orgIdToSend = selectedOrganization;
      } else {
        orgIdToSend = login.data.orgId;
      }

      const isUpdate =
        selectedIndex !== null && list[selectedIndex]?.templateId;
      const templateId = isUpdate ? list[selectedIndex].templateId : undefined;

      // Check if there are any existing PowerPoint templates for this organization
      const existingPptTemplatesForOrg = list.filter(
        (tpl) =>
          tpl.templateType === "powerpoint" &&
          tpl.orgId === orgIdToSend &&
          (!isUpdate || tpl.templateId !== templateId), // Exclude current template if updating
      );

      // Auto-set as default if this will be the only PowerPoint template for the org
      const shouldBeDefault =
        pptSwitch || existingPptTemplatesForOrg.length === 0;

      const payload = new FormData();
      if (templateId) payload.append("templateId", templateId);
      payload.append("orgId", orgIdToSend);
      payload.append("userId", login.data.id);
      payload.append(
        "templateName",
        templateData.templateName || file.name.replace(/\.(pptx|potx)$/i, ""),
      );
      payload.append("templateType", "powerpoint");
      payload.append("file", file);
      payload.append("isGlobalTemplate", isGlobalAdmin ? "true" : "false");
      payload.append("templateUploadedBy", login?.data?.role);
      payload.append("isDefaultPPT", shouldBeDefault ? "true" : "false");

      const resp: any = await uploadPptTemplate(payload);

      setLoading(false);

      if (resp?.hasError) {
        setAlert(resp.message || "Upload failed", "error");
        return resp;
      }

      setAlert(resp.message || "Upload successful", "success");

      if (resp.data) {
        const data = resp.data || {};
        const newTemplate = {
          templateId: data._id || data.templateId || null,
          orgId: data.orgId || orgIdToSend,
          userId: data.uploadedById || data.userId || login.data.id,
          templateName:
            data.templateName ||
            templateData.templateName ||
            file.name.replace(/\.(pptx|potx)$/i, ""),
          templateType: data.metadata?.templateType || "powerpoint",
          version: data.version,
          sections: data.sections || [],
          rawMarkdown: data.rawMarkdown || data.templateContent || "",
          templateContent: data.blobUrl || data.templateContent || "",
          templateUploadedBy: data.uploadedBy || login?.data?.role,
          isGlobalTemplate:
            (data.metadata && data.metadata.isGlobalTemplate) === true ||
            false,
          isDefaultPPT: data.isDefaultPPT || false,
          versions: data.versions || [],
        };

        setList((prev) => {
          let updatedList;
          if (isUpdate) {
            // replace existing
            updatedList = prev.map((tpl) =>
              tpl.templateId === newTemplate.templateId ? newTemplate : tpl,
            );
          } else {
            // append new
            updatedList = [...prev, newTemplate];
          }

          // If this template is set as default, ensure all other PPT templates in the same org are not default
          if (
            newTemplate.isDefaultPPT &&
            newTemplate.templateType === "powerpoint"
          ) {
            updatedList = updatedList.map((tpl) => {
              if (
                tpl.templateType === "powerpoint" &&
                tpl.orgId === newTemplate.orgId &&
                tpl.templateId !== newTemplate.templateId
              ) {
                return { ...tpl, isDefaultPPT: false };
              }
              return tpl;
            });
          }

          return updatedList;
        });

        // Cache newly uploaded PPT (best-effort)
        if (newTemplate.templateId && newTemplate.templateContent) {
          backgroundFetchAndCache(
            newTemplate.templateId,
            newTemplate.templateContent,
          ).catch(() => {});
        }

        // select the new/updated template so preview shows
        setSelectedIndex((prevIdx) => {
          if (isUpdate && prevIdx !== null) return prevIdx;
          return isUpdate ? prevIdx : list.length || 0;
        });

        // update templateData so preview shows immediately
        setTemplateData({
          templateName: newTemplate.templateName,
          templateType: newTemplate.templateType,
          templateContent: newTemplate.templateContent || "",
          rawMarkdown: newTemplate.rawMarkdown || "",
        });
        setPowerPointFile(null);

        // Update the switch to reflect the actual isDefaultPPT value from backend
        setPptSwitch(newTemplate.isDefaultPPT || false);
      }

      return resp;
    } catch (err) {
      setLoading(false);
      console.error("uploadSelectedPpt error", err);
      setAlert("Upload failed", "error");
      throw err;
    }
  };

  const handleImportFile = async (file: File) => {
    setImportModalOpen(false);
    setImportAnalyzing(true);
    const payload = new FormData();
    payload.append("file", file);
    if (activeTab === 6) payload.append("mode", "skill");

    try {
      const resp: any = await analyzeUploadedTemplate(payload);

      if (activeTab === 6) {
        const rawMarkdown = resp?.rawMarkdown ?? resp?.data?.rawMarkdown;
        if (resp?.hasError || typeof rawMarkdown !== "string" || !rawMarkdown.trim()) {
          setAlert(resp?.message || "We couldn't extract skill instructions from that file.", "error");
          return;
        }

        const skillName = file.name.replace(/\.[^.]+$/, "").trim() || "Uploaded skill";
        const orgIdToSend = isGlobalAdmin
          ? selectedOrganization === "All Organisation"
            ? "0"
            : selectedOrganization
          : login.data.orgId;
        const saveResp: any = await createDocTemplate({
          orgId: orgIdToSend,
          userId: login.data.id,
          templateName: skillName,
          templateType: "skill",
          templateContent: rawMarkdown,
          isGlobalTemplate: isGlobalAdmin ? "true" : "false",
          templateUploadedBy: login?.data?.role,
        });

        if (saveResp?.hasError || !saveResp?.data) {
          setAlert(saveResp?.message || "The skill could not be saved.", "error");
          return;
        }

        const data = saveResp.data;
        const newSkill: DocTemplateListItem = {
          templateId: data._id || data.templateId,
          orgId: data.orgId || orgIdToSend,
          userId: data.userId || login.data.id,
          templateName: data.templateName || skillName,
          templateType: "skill",
          version: data.version,
          sections: data.sections || [],
          rawMarkdown: data.rawMarkdown || data.templateContent || rawMarkdown,
          templateContent: data.templateContent || data.rawMarkdown || rawMarkdown,
          templateUploadedBy: data.templateUploadedBy || login?.data?.role,
          isGlobalTemplate:
            data.isGlobalTemplate === true || data.isGlobalTemplate === "true",
          isClonedTemplate: false,
          versions: data.versions || [],
        };

        setList((currentList) => {
          const nextList = [...currentList, newSkill];
          setSelectedIndex(nextList.length - 1);
          return nextList;
        });
        setMonacoContent(rawMarkdown);
        setTemplateSections(hydrateTemplateSections(rawMarkdown, newSkill.sections as never));
        sectionsParseSourceRef.current = normalizeMarkdown(rawMarkdown);
        setTemplateData({
          templateName: newSkill.templateName,
          templateType: "skill",
          templateContent: newSkill.templateContent || "",
          rawMarkdown,
        });
        setEditorMode("markdown");
        setSectionsDirty(false);
        setAlert(`Skill "${newSkill.templateName}" saved successfully.`, "success");
        return;
      }

      const sections =
        (resp as { sections?: TemplateSection[] })?.sections ??
        (resp as { data?: { sections?: TemplateSection[] } })?.data?.sections;

      if (resp?.hasError || !sections || !Array.isArray(sections)) {
        setAlert(resp?.message || "Analysis failed. Please try again.", "error");
        return;
      }

      if (!sections.length) {
        setAlert(
          "We couldn't detect any sections in this document. Please check the file has clear headings and try again.",
          "error",
        );
        return;
      }

      setTemplateSections(sections);
      const md = templateSectionsToMarkdown(sections);
      setMonacoContent(md);
      sectionsParseSourceRef.current = normalizeMarkdown(md);
      setEditorMode("sections");
      setSectionsDirty(true);
      setSectionsCollapseToken((t) => t + 1);
      setAlert(
        `Imported ${sections.length} section(s) from ${file.name}. Review and save when ready.`,
        "success",
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Analysis failed. Please try again.";
      setAlert(message, "error");
    } finally {
      setImportAnalyzing(false);
    }
  };

  const handleUploadBoxClick = () => {
    if (!isReadOnlyMode) {
      // Check if global admin has selected an organization first for PowerPoint uploads
      if (
        isGlobalAdmin &&
        (activeTab === 2 || templateData.templateType === "powerpoint") &&
        (!selectedOrganization || selectedOrganization === "All Organisation")
      ) {
        setAlert(
          "Please select a specific organization before uploading PowerPoint templates. ",
          "error",
        );
        return;
      }
      fileInputRef.current?.click();
    }
  };

  const getTemplateTypeFromTab = (tabIndex: number): string => {
    if (tabIndex === 0) return "file note";
    if (tabIndex === 1) return "fact find";
    if (tabIndex === 2) return "powerpoint";
    if (tabIndex === 3) return "agreements";
    if (tabIndex === 4) return "factfind-ontology";
    if (tabIndex === 5) return "email";
    if (tabIndex === 6) return "skill";
    return "file note";
  };

  const handleUpload = async () => {
    // Get template type from active tab instead of templateData
    const currentTemplateType = getTemplateTypeFromTab(activeTab);

    // STRICT VALIDATION: Global admin must select specific org for PowerPoint templates
    if (
      currentTemplateType === "powerpoint" &&
      isGlobalAdmin &&
      (!selectedOrganization || selectedOrganization === "All Organisation")
    ) {
      setAlert(
        "Please select a specific organization before uploading PowerPoint templates.",
        "error",
      );
      return;
    }

    // Check required fields
    if (!templateData.templateName.trim()) {
      setAlert("Template Name is required.", "error");
      return;
    }

    if (
      currentTemplateType === "powerpoint" &&
      !powerPointFile &&
      selectedIndex === null
    ) {
      setAlert("Please select a .pptx file to upload.", "error");
      return;
    }

    setLoading(true);

    const usesSectionEditorForSave =
      (activeTab === 0 || activeTab === 1 || activeTab === 5 || activeTab === 6) &&
      currentTemplateType !== "powerpoint";
    const markdownForSave = usesSectionEditorForSave
      ? editorMode === "markdown" ||
        (editorMode === "sections" && !sectionsDirty)
        ? monacoContent
        : templateSectionsToMarkdown(templateSections)
      : monacoContent;

    // Determine if this is an update or create based on whether we have a selected template
    const isUpdate = selectedIndex !== null && list[selectedIndex]?.templateId;

    // Determine orgId to send for both create and update flows
    let orgIdToSend;
    if (isGlobalAdmin) {
      // For global admin:
      // - If "All Organisation" is selected, send "0" (seed template)
      // - Otherwise, send the specific org ID
      if (selectedOrganization === "All Organisation") {
        orgIdToSend = "0";
      } else {
        orgIdToSend = selectedOrganization;
      }
    } else {
      orgIdToSend = login.data.orgId;
    }

    if (!isUpdate) {
      try {
        let payload: any;

        if (currentTemplateType === "powerpoint") {
          // --- PowerPoint Upload (FormData) ---
          // Backend expects multipart field name 'file' and endpoint POST /ppt-templates/upload
          payload = new FormData();
          // include org and user so the server can validate/update correctly
          payload.append("orgId", orgIdToSend);
          payload.append("userId", login.data.id);
          payload.append("templateName", templateData.templateName);
          payload.append("templateType", currentTemplateType);
          if (powerPointFile) {
            // backend multer.single('file') expects this field name
            payload.append("file", powerPointFile);
          }
          // include flags/metadata expected by the server
          payload.append("isGlobalTemplate", isGlobalAdmin ? "true" : "false");
          payload.append("templateUploadedBy", login?.data?.role);
        } else {
          // --- Text Template Upload (JSON) ---
          payload = {
            orgId: orgIdToSend,
            userId: login.data.id,
            templateName: templateData.templateName,
            templateType: currentTemplateType,
            templateContent: markdownForSave,
            isGlobalTemplate: isGlobalAdmin ? "true" : "false",
            templateUploadedBy: login?.data?.role,
          };
        }

        const resp: any =
          currentTemplateType === "powerpoint"
            ? await uploadPptTemplate(payload)
            : await createDocTemplate(payload);

        setLoading(false);
        setAlert(resp.message, resp.hasError ? "error" : "success");

        if (!resp.hasError && resp.data) {
          // Add the new template to the list and select it
          // Normalize backend response for both doc templates and powerpoint templates
          const data = resp.data || {};
          const newTemplate = {
            templateId: data._id || data.templateId || null,
            orgId: data.orgId || orgIdToSend,
            userId: data.uploadedById || data.userId || login.data.id,
            templateName: data.templateName || templateData.templateName,
            templateType:
              data.metadata?.templateType ||
              templateData.templateType ||
              data.templateType,
            version: data.version,
            sections: data.sections || [],
            // For PowerPoint the URL is returned as blobUrl
            rawMarkdown: data.rawMarkdown || data.templateContent || "",
            templateContent:
              data.blobUrl || data.templateContent || data.rawMarkdown || "",
            templateUploadedBy: data.uploadedBy || login?.data?.role,
            isGlobalTemplate:
              (data.metadata && data.metadata.isGlobalTemplate) === true ||
              data.isGlobalTemplate === "true" ||
              data.isGlobalTemplate === true ||
              false,
            isDefaultPPT: data.isDefaultPPT || false,
            versions: data.versions || [],
          };
          setList((prev) => {
            let newList = [...prev, newTemplate];

            // If this template is set as default, ensure all other PPT templates in the same org are not default
            if (
              newTemplate.isDefaultPPT &&
              newTemplate.templateType === "powerpoint"
            ) {
              newList = newList.map((tpl) => {
                if (
                  tpl.templateType === "powerpoint" &&
                  tpl.orgId === newTemplate.orgId &&
                  tpl.templateId !== newTemplate.templateId
                ) {
                  return { ...tpl, isDefaultPPT: false };
                }
                return tpl;
              });
            }

            // Set the selected index to the new template
            setSelectedIndex(newList.length - 1);
            setPowerPointFile(null);
            // Update templateData to reflect the newly created template
            const savedMd =
              newTemplate.rawMarkdown ||
              newTemplate.templateContent ||
              markdownForSave;
            setMonacoContent(savedMd);
            setTemplateSections(
              hydrateTemplateSections(savedMd, newTemplate.sections as never),
            );
            sectionsParseSourceRef.current = normalizeMarkdown(savedMd);
            setEditorMode("sections");
            setSectionsDirty(false);
            setTemplateData({
              templateName: newTemplate.templateName,
              templateType: newTemplate.templateType,
              templateContent: newTemplate.templateContent || "",
              rawMarkdown: savedMd,
            });
            return newList;
          });
        }
      } catch (err) {
        setLoading(false);
        setAlert("Upload failed", "error");
        console.error("Upload error:", err);
      }
    } else {
      // --- UPDATE EXISTING TEMPLATE ---
      const templateId = list[selectedIndex].templateId;
      try {
        let payload: any;

        if (currentTemplateType === "powerpoint") {
          // --- PowerPoint Update (FormData) ---
          // Backend supports update via POST /ppt-templates/upload when templateId is provided
          payload = new FormData();
          // Required fields for server-side update
          payload.append("templateId", templateId);
          payload.append("orgId", orgIdToSend);
          payload.append("userId", login.data.id);
          payload.append("templateName", templateData.templateName);
          payload.append("templateType", currentTemplateType);
          payload.append("isDefaultPPT", pptSwitch ? "true" : "false");
          if (powerPointFile) {
            // use the field name 'file' to match multer.single('file') on the server
            payload.append("file", powerPointFile);
          }
          if (isGlobalAdmin) {
            payload.append(
              "isGlobalTemplate",
              list[selectedIndex].isGlobalTemplate.toString(),
            );
          }
          payload.append("templateUploadedBy", login?.data?.role);
        } else {
          // --- Text Template Update (JSON) ---
          payload = {
            rawMarkdown: markdownForSave,
            templateName: templateData.templateName,
            templateType: currentTemplateType,
          };
          if (isGlobalAdmin) {
            payload.isGlobalTemplate = list[selectedIndex].isGlobalTemplate;
          }
        }

        // For PowerPoint updates we use the server-side upload endpoint which handles replace/update
        const resp: any =
          currentTemplateType === "powerpoint"
            ? await uploadPptTemplate(payload)
            : await updateDocTemplate(templateId, payload);

        setLoading(false);
        setAlert(
          resp.message || "Update complete",
          resp.hasError ? "error" : "success",
        );

        if (!resp.hasError && resp.data) {
          const data = resp.data || {};
          // normalize updated item for UI
          const updatedTemplate = {
            templateId: data._id || data.templateId || templateId,
            orgId: data.orgId || list[selectedIndex].orgId,
            userId:
              data.uploadedById || data.userId || list[selectedIndex].userId,
            templateName: data.templateName || templateData.templateName,
            templateType:
              data.metadata?.templateType ||
              templateData.templateType ||
              list[selectedIndex].templateType,
            version: data.version,
            sections: data.sections || list[selectedIndex].sections || [],
            templateContent:
              data.blobUrl ||
              data.templateContent ||
              list[selectedIndex].templateContent ||
              "",
            rawMarkdown:
              data.rawMarkdown ||
              data.templateContent ||
              list[selectedIndex].rawMarkdown ||
              "",
            templateUploadedBy:
              data.uploadedBy || list[selectedIndex].templateUploadedBy,
            isGlobalTemplate:
              (data.metadata && data.metadata.isGlobalTemplate) === true ||
              list[selectedIndex].isGlobalTemplate ||
              false,
            isDefaultPPT:
              data.isDefaultPPT !== undefined
                ? data.isDefaultPPT
                : list[selectedIndex].isDefaultPPT || false,
            versions: data.versions || list[selectedIndex].versions || [],
          };

          // Update the template in the list while preserving the selection
          setList((prev) => {
            let updatedList = prev.map((tpl, idx) =>
              idx === selectedIndex ? updatedTemplate : tpl,
            );

            // If this template is set as default, ensure all other PPT templates in the same org are not default
            if (
              updatedTemplate.isDefaultPPT &&
              updatedTemplate.templateType === "powerpoint"
            ) {
              updatedList = updatedList.map((tpl) => {
                if (
                  tpl.templateType === "powerpoint" &&
                  tpl.orgId === updatedTemplate.orgId &&
                  tpl.templateId !== updatedTemplate.templateId
                ) {
                  return { ...tpl, isDefaultPPT: false };
                }
                return tpl;
              });
            }

            return updatedList;
          });

          // Update the template data to reflect the changes
          const savedMd =
            updatedTemplate.rawMarkdown ||
            updatedTemplate.templateContent ||
            markdownForSave;
          setMonacoContent(savedMd);
          setTemplateSections(
            hydrateTemplateSections(savedMd, updatedTemplate.sections as never),
          );
          sectionsParseSourceRef.current = normalizeMarkdown(savedMd);
          setEditorMode("sections");
          setSectionsDirty(false);
          setTemplateData({
            templateName: updatedTemplate.templateName,
            templateType: updatedTemplate.templateType,
            templateContent: updatedTemplate.templateContent || "",
            rawMarkdown: savedMd,
          });
          // Cache updated PPT blob (best-effort)
          if (updatedTemplate.templateId && updatedTemplate.templateContent) {
            backgroundFetchAndCache(
              updatedTemplate.templateId,
              updatedTemplate.templateContent,
            ).catch(() => {});
          }
          setPowerPointFile(null);
        }
      } catch (err) {
        setLoading(false);
        setAlert("Update failed. Please try again.", "error");
        console.error("Update error:", err);
      }
    }
  };

  const handleDelete = async (templateId: string) => {
    try {
      // Find the template to check permissions
      const templateToDelete = list.find(
        (tpl) => tpl.templateId === templateId,
      );

      // Prevent org admin from deleting templates created by global admin
      if (
        !isGlobalAdmin &&
        templateToDelete?.templateUploadedBy === "global admin"
      ) {
        setAlert(
          "You cannot delete templates created by Global Admin. You can only delete templates you created.",
          "error",
        );
        return;
      }

      // choose delete call depending on template type
      if (templateToDelete?.templateType === "powerpoint") {
        await deletePptTemplate(templateId);
      } else {
        await deleteDocTemplate(templateId);
      }

      // Find the index of the deleted template
      const deletedIndex = list.findIndex(
        (tpl) => tpl.templateId === templateId,
      );

      // Store the current active tab before deletion
      const currentTabType = getTemplateTypeFromTab(activeTab);

      if (selectedIndex === deletedIndex) {
        // If the deleted template was selected, clear the selection
        setSelectedIndex(null);
        setMonacoContent("");
        setTemplateSections([createEmptySection()]);
        setEditorMode("sections");
        setSectionsDirty(false);
        setPowerPointFile(null);
        setTemplateData({
          templateName: "",
          templateType: currentTabType, // Keep the current tab's template type
          templateContent: "",
          rawMarkdown: "",
        });
      } else if (selectedIndex !== null && selectedIndex > deletedIndex) {
        // If the selected template is after the deleted one, adjust the index
        setSelectedIndex(selectedIndex - 1);
      }

      // Remove the deleted template from the list
      setList((prev) => prev.filter((tpl) => tpl.templateId !== templateId));

      setAlert("Template deleted successfully", "success");
    } catch (err) {
      console.error("Failed to delete document", err);
      setAlert("Failed to delete template", "error");
    }
  };

  const getTemplatesList = async () => {
    if (!selectedOrganization) {
      setList([]);
      return;
    }

    try {
      // Set loading state specifically for template switching
      setTemplatesLoading(true);

      // Clear template list immediately to avoid showing old templates during loading
      setList([]);

      // Clear previous template selection and related state when switching organizations
      setSelectedIndex(null);
      setTemplateData({
        templateName: "",
        templateType: "",
        templateContent: "",
        rawMarkdown: "",
      });
      setPowerPointFile(null);
      setPptSwitch(false);
      setMonacoContent("");
      setTemplateSections([createEmptySection()]);
      setEditorMode("sections");
      setSectionsDirty(false);
      setDecorations([]);
      setSearchText("");

      // fetch both text templates and powerpoint templates and merge
      const [respText, respPpt] = await Promise.all([
        fetchAllDocTemplates(),
        fetchPptTemplates(),
      ]);
      const respTextAny: any = respText;
      const respPptAny: any = respPpt;
      if (respTextAny?.hasError) setAlert(respTextAny.message);
      if (respPptAny?.hasError) setAlert(respPptAny.message);

      const docs = Array.isArray(respTextAny?.data) ? respTextAny.data : [];
      const ppts = Array.isArray(respPptAny?.data) ? respPptAny.data : [];

      // filter by selectedOrganization (or include orgId === '0' seed templates)
      const filterForOrg = (arr: any[]) =>
        selectedOrganization === "All Organisation"
          ? arr
          : arr.filter(
              (item) =>
                item.orgId === selectedOrganization || item.orgId === "0",
            );

      // For PowerPoint templates, apply additional filtering for global admin
      const filterForOrgPpt = (arr: any[]) => {
        // For global admin, don't show any PowerPoint templates when "All Organisation" is selected
        if (
          selectedOrganization === "All Organisation" &&
          login?.data?.role === "global admin"
        ) {
          return [];
        }
        return filterForOrg(arr);
      };

      const filteredDocs = filterForOrg(docs);
      const filteredPpts = filterForOrgPpt(ppts);

      // normalize ppt items to the UI shape
      const mappedPpts = filteredPpts.map((item: any) => ({
        templateId: item._id,
        orgId: item.orgId,
        userId: item.uploadedById,
        templateName: item.templateName,
        templateType: item.metadata?.templateType || "powerpoint",
        version: undefined,
        sections: [],
        templateContent: item.blobUrl || "",
        rawMarkdown: "",
        templateUploadedBy: item.uploadedBy || "orgadmin",
        isGlobalTemplate:
          (item.metadata && item.metadata.isGlobalTemplate) === true || false,
        isClonedTemplate: false,
        versions: [],
        isDefaultPPT: item.isDefaultPPT || false,
      }));

      // combine text templates and ppt templates
      setList([...filteredDocs, ...mappedPpts]);

      // In background: prefetch PPT blobs and save to cache so previews work even if remote URL later expires
      (async () => {
        try {
          for (const item of mappedPpts) {
            if (item.templateId && item.templateContent) {
              // Don't await each one - do them in parallel but guarded
              backgroundFetchAndCache(
                item.templateId,
                item.templateContent,
              ).catch(() => {});
            }
          }
        } catch (e) {
          // swallow errors - caching is best-effort
          console.debug("ppt prefetch error", e);
        }
      })();
    } catch (err) {
      console.error("getTemplatesList error", err);
      setAlert("Failed to fetch templates", "error");
      setList([]);
    } finally {
      // Clear template switching loading state
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    getTemplatesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization, setAlert]);

  useSkillChangeListener(() => {
    getTemplatesList();
  });

  useEffect(() => {
    if (
      editorInstance &&
      searchText &&
      templateData.templateType !== "powerpoint" &&
      (!usesSectionEditor || editorMode === "markdown")
    ) {
      const model = (editorInstance as any).getModel();
      if (!model) return;
      const matches = model.findMatches(
        searchText,
        false,
        false,
        false,
        null,
        true,
      );
      const newDecorations = matches.map((match) => ({
        range: match.range,
        options: {
          inlineClassName: "search-highlight",
        },
      }));
      setDecorations(
        (editorInstance as any).deltaDecorations(decorations, newDecorations),
      );
    } else if (editorInstance) {
      setDecorations((editorInstance as any).deltaDecorations(decorations, []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, editorInstance, templateData.templateType, usesSectionEditor, editorMode]);

  const handleTemplateSelect = (idx: number) => {
    setSelectedIndex(idx);
    const tpl = list[idx];
    setPowerPointFile(null);

    // Set the default PPT switch state based on the selected template
    setPptSwitch(tpl.isDefaultPPT || false);

    // Automatically switch to the correct tab based on template type
    if (tpl.templateType === "file note") {
      setActiveTab(0);
    } else if (tpl.templateType === "fact find") {
      setActiveTab(1);
    } else if (tpl.templateType === "powerpoint") {
      setActiveTab(2);
    } else if (tpl.templateType === "email") {
      setActiveTab(5);
    } else if (tpl.templateType === "skill") {
      setActiveTab(6);
    }

    const content = tpl.rawMarkdown || tpl.templateContent || "";

    if (tpl.templateType !== "powerpoint") {
      setMonacoContent(content);
      setTemplateSections(
        hydrateTemplateSections(content, tpl.sections as never),
      );
      sectionsParseSourceRef.current = normalizeMarkdown(content);
      setEditorMode("sections");
      setSectionsDirty(false);
      setTemplateData({
        templateName: tpl.templateName,
        templateType: tpl.templateType,
        templateContent: content,
        rawMarkdown: content,
      });
      return;
    }

    // For PowerPoint: Use the direct Azure blob URL for Office Online preview
    // The content variable contains the blobUrl from the API response
    if (tpl.templateType === "powerpoint") {
      setTemplateData({
        templateName: tpl.templateName,
        templateType: tpl.templateType,
        templateContent: content, // This is the blobUrl from API
        rawMarkdown: tpl.rawMarkdown || "",
      });
      setMonacoContent("");

      // Background cache the file for potential future use (but don't use cached URLs for preview)
      if (tpl.templateId && content) {
        backgroundFetchAndCache(tpl.templateId, content).catch(() => {});
      }
      return;
    }

    // For non-PowerPoint templates, we can use cached content or direct content
    setTemplateData({
      templateName: tpl.templateName,
      templateType: tpl.templateType,
      templateContent: content,
      rawMarkdown: content,
    });
    setMonacoContent(content);
  };

  // Background helper: fetch remote PPT URL and save blob to cache (best-effort)
  const backgroundFetchAndCache = async (templateId: string, url: string) => {
    if (!templateId || !url) return;
    try {
      const resp = await fetch(url);
      if (!resp.ok) return;
      const blob = await resp.blob();
      // Only cache if we actually got a blob
      if (blob && blob.size > 0) {
        await savePptToCache(templateId, blob);
      }
    } catch (e) {
      // ignore network or CORS errors - caching is best-effort
      console.debug("backgroundFetchAndCache error", e);
    }
  };

  const handleCloneTemplate = async (idx: number) => {
    const template = list[idx];
    if (!template) return;
    try {
      const payload: any = { templateId: template.templateId };

      if (isGlobalAdmin) {
        payload.orgId = "0";
        payload.userId = "0";
        payload.isClonedTemplate = false;
        payload.isGlobalTemplate = true;
      } else {
        payload.orgId = login.data.orgId;
        payload.userId = login.data.id;
        payload.isClonedTemplate = true;
        payload.isGlobalTemplate = false;
      }

      const resp = await cloneDocTemplate(payload);
      const result = resp as {
        hasError?: boolean;
        message?: string;
        data?: any;
      };
      setAlert(result.message || "", result.hasError ? "error" : "success");
      if (!result.hasError && result.data) {
        setList((prev) => [...prev, result.data]);
        getTemplatesList();
      }
    } catch (err) {
      setAlert("Failed to clone template", "error");
    }
  };

  const handleToggleGlobalTemplate = async (idx: number) => {
    if (!isGlobalAdmin) return;
    try {
      const resp: any = await toggleDocTemplateGlobal(list[idx].templateId);
      setAlert(resp.message || "", resp.hasError ? "error" : "success");
      if (!resp.hasError && resp.data) {
        // Update only the toggled item for better UX
        const updatedList = [...list];
        updatedList[idx] = resp.data;
        setList(updatedList);
      }
    } catch (err) {
      setAlert("Failed to toggle global state", "error");
    }
  };

  const handleSetDefaultPpt = async (newValue: boolean) => {
    // Only call API if a template is selected
    if (selectedIndex === null || !list[selectedIndex]?.templateId) {
      // If no template selected, just update local state
      setPptSwitch(newValue);
      return;
    }

    const templateId = list[selectedIndex].templateId;

    // Determine orgId
    let orgIdToSend;
    if (isGlobalAdmin) {
      // Validation should prevent this, but double-check
      if (!selectedOrganization || selectedOrganization === "All Organisation") {
        setAlert(
          "ERROR: Please select a specific organization for PowerPoint template operations.",
          "error",
        );
        return;
      }
      orgIdToSend = selectedOrganization;
    } else {
      orgIdToSend = login.data.orgId;
    }

    try {
      const resp: any = await setDefaultPptTemplate(templateId, {
        isDefaultPPT: newValue,
        orgId: orgIdToSend,
        userId: login.data.id,
      });

      if (resp?.hasError) {
        setAlert(resp.message || "Failed to update default template", "error");
        return;
      }

      setAlert(
        resp.message || "Default template updated successfully",
        "success",
      );
      setPptSwitch(newValue);

      // Update the list to reflect the change
      setList((prevList) =>
        prevList.map((tpl, idx) => {
          if (idx === selectedIndex) {
            // Set the selected template as default/not default
            return { ...tpl, isDefaultPPT: newValue };
          } else if (
            tpl.templateType === "powerpoint" &&
            tpl.orgId === prevList[selectedIndex]?.orgId &&
            newValue === true
          ) {
            // If we're setting a template as default, unset all other PowerPoint templates in the same org
            return { ...tpl, isDefaultPPT: false };
          }
          return tpl;
        }),
      );
    } catch (err) {
      console.error("Failed to toggle default template", err);
      setAlert("Failed to update default template", "error");
    }
  };

  // Add a "New Template" function to clear the selection
  const handleNewTemplate = () => {
    setSelectedIndex(null);
    setMonacoContent("");
    setTemplateSections([createEmptySection()]);
    setEditorMode("sections");
    setSectionsDirty(false);
    setPowerPointFile(null);
    setPptSwitch(false); // Reset default PPT toggle
    setTemplateData({
      templateName: "",
      templateType: "",
      templateContent: "",
      rawMarkdown: "",
    });
    setSearchText("");
  };

  /** Resets template state for a newly selected tab, then auto-selects that tab's first template. */
  const resetForTabChange = (newValue: number) => {
    const templateType = getTemplateTypeFromTab(newValue);
    setSelectedIndex(null);
    setMonacoContent("");
    setTemplateSections([createEmptySection()]);
    setEditorMode("sections");
    setSectionsDirty(false);
    setPowerPointFile(null);
    setPptSwitch(false);
    setSearchText("");
    setTemplateData({ templateName: "", templateType, templateContent: "", rawMarkdown: "" });

    // Skip auto-select for Documents (3) and Fact Find New (4) tabs
    if (list.length > 0 && newValue !== 3 && newValue !== 4) {
      const firstTemplateOfTab = list.findIndex(
        (tpl) => tpl.templateType === getTemplateTypeFromTab(newValue),
      );
      if (firstTemplateOfTab !== -1) {
        setTimeout(() => {
          handleTemplateSelect(firstTemplateOfTab);
        }, 0);
      }
    }
  };

  const handleEditorModeChange = (value: "sections" | "markdown") => {
    if (!value || value === editorMode) return;

    if (value === "markdown") {
      const md = sectionsDirty
        ? templateSectionsToMarkdown(templateSections)
        : coalesceMarkdown(monacoContent, templateSections);
      setMonacoContent(md);
      sectionsParseSourceRef.current = normalizeMarkdown(md);
    } else {
      const md = coalesceMarkdown(monacoContent, templateSections);
      const normalized = normalizeMarkdown(md);
      const apiSections =
        selectedIndex !== null ? list[selectedIndex]?.sections : undefined;
      if (normalized && normalized !== sectionsParseSourceRef.current) {
        setTemplateSections(hydrateTemplateSections(md, apiSections as never));
        sectionsParseSourceRef.current = normalized;
      }
      setSectionsDirty(false);
    }
    setEditorMode(value);
  };

  const handleSectionsChange = (sections: TemplateSection[]) => {
    setTemplateSections((prev) => {
      const contentChanged =
        sections.length !== prev.length ||
        sections.some((s, i) => {
          const p = prev[i];
          if (!p || p.id !== s.id) return true;
          return (
            p.title !== s.title ||
            p.prompt !== s.prompt ||
            p.textPrefix !== s.textPrefix ||
            p.sectionType !== s.sectionType ||
            p.headingLevel !== s.headingLevel ||
            p.placeholder !== s.placeholder ||
            JSON.stringify(p.assignedTopics) !== JSON.stringify(s.assignedTopics)
          );
        });
      if (contentChanged) {
        const scrollTitle = findChangedSectionTitle(prev, sections);
        if (scrollTitle) {
          setPreviewScrollTarget(scrollTitle);
        }
        setSectionsDirty(true);
      }
      return sections;
    });
  };

  const handleEditorMount = (editor: any) => {
    setEditorInstance(editor);
    monacoRef.current = editor;
  };

  /** onChange for the section-builder's markdown toggle view — also drives scroll-to-heading sync. */
  const handleMonacoContentChange = (value: string | undefined) => {
    if (editorModeRef.current !== "markdown") return;
    setMonacoContent(value ?? "");

    const editor: any = monacoRef.current || editorInstance;
    if (editor?.getModel && editor?.getPosition) {
      window.clearTimeout(monacoPreviewScrollTimer.current);
      monacoPreviewScrollTimer.current = window.setTimeout(() => {
        const model = editor.getModel();
        const pos = editor.getPosition();
        if (!model || !pos) return;
        for (let line = pos.lineNumber; line >= 1; line--) {
          const match = model.getLineContent(line).match(/^#{1,6}\s+(.+)$/);
          if (match) {
            setPreviewScrollTarget(match[1].trim());
            break;
          }
        }
      }, 350);
    }
  };

  // Derived flags used by the preview UI
  const isPpt = templateData.templateType === "powerpoint";
  const hasLocalPptFile = powerPointFile !== null;
  const isExistingPpt = selectedIndex !== null && isPpt && !hasLocalPptFile;
  // Use templateContent as it's the dedicated field for the URL/content
  const pptUrl = isExistingPpt ? templateData.templateContent : null;

  return {
    loading,
    templatesLoading,
    templateData,
    setTemplateData,
    list,
    selectedIndex,
    setSelectedIndex,
    isGlobalAdmin,
    isReadOnlyMode,
    pendingDeleteTemplateId,
    setPendingDeleteTemplateId,

    searchText,
    setSearchText,
    usesSectionEditor,
    editorMarkdown,
    previewHtml,
    monacoContent,
    setMonacoContent,
    handleMonacoContentChange,
    templateSections,
    setTemplateSections,
    handleSectionsChange,
    editorMode,
    setEditorMode,
    handleEditorModeChange,
    sectionsDirty,
    setSectionsDirty,
    sectionsCollapseToken,
    editorInstance,
    handleEditorMount,
    previewContainerRef,
    sectionsParseSourceRef,

    powerPointFile,
    setPowerPointFile,
    fileInputRef,
    pptSwitch,
    handleSetDefaultPpt,
    importModalOpen,
    setImportModalOpen,
    importAnalyzing,
    handleFileSelect,
    handleUploadBoxClick,
    handleImportFile,

    isPpt,
    hasLocalPptFile,
    isExistingPpt,
    pptUrl,

    handleTemplateSelect,
    handleUpload,
    handleDelete,
    handleCloneTemplate,
    handleNewTemplate,
    handleToggleGlobalTemplate,
    resetForTabChange,
  };
};
